import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, LinkIcon, XIcon } from 'lucide-react';
import { FormSection, TextField, SelectField, DisplayField } from '../components/FormComponents';
import { mockProducts } from '../data/mockData';
const mockAppItems = [{
  id: 'APP-2025-001',
  name: 'Laptop Pro X',
  type: 'Goods',
  qty: 20,
  unitPrice: 1200,
  total: 24000,
  fiscalYear: 'FY 2025/26'
}, {
  id: 'APP-2025-002',
  name: 'A4 Printer Paper',
  type: 'Goods',
  qty: 100,
  unitPrice: 25,
  total: 2500,
  fiscalYear: 'FY 2025/26'
}, {
  id: 'APP-2025-003',
  name: 'Warehouse Expansion',
  type: 'Works',
  qty: 1,
  unitPrice: 350000,
  total: 350000,
  fiscalYear: 'FY 2025/26'
}, {
  id: 'APP-2025-004',
  name: 'IT Consulting',
  type: 'Service',
  qty: 1,
  unitPrice: 60000,
  total: 60000,
  fiscalYear: 'FY 2025/26'
}, {
  id: 'APP-2025-005',
  name: 'Office Renovation',
  type: 'Works',
  qty: 1,
  unitPrice: 90000,
  total: 90000,
  fiscalYear: 'FY 2025/26'
}, {
  id: 'APP-2024-001',
  name: 'Laptop Pro X',
  type: 'Goods',
  qty: 5,
  unitPrice: 1200,
  total: 6000,
  fiscalYear: 'FY 2024/25'
}, {
  id: 'APP-2024-002',
  name: 'Ink Cartridges HP 61XL',
  type: 'Goods',
  qty: 200,
  unitPrice: 35,
  total: 7000,
  fiscalYear: 'FY 2024/25'
}];
export function PurchaseRequisitionForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    prNo: 'PR-AUTO-GEN',
    originator: 'Admin User',
    departmentCode: '',
    project: '',
    priority: 'Medium',
    estimatedDeliveryDate: '',
    requisitionFor: 'Goods',
    procurementMethod: 'Direct Purchase'
  });
  const createEmptyLineItem = () => ({
    id: Date.now().toString(),
    product: '',
    qty: 1,
    rate: 0,
    estimatedCost: 0,
    specification: '',
    appReference: ''
  });
  const [lineItems, setLineItems] = useState([{
    ...createEmptyLineItem(),
    id: '1'
  }]);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [activeLineItemId, setActiveLineItemId] = useState<string | null>(null);
  const [appFiscalYear, setAppFiscalYear] = useState('FY 2025/26');
  // Reset line items when requisitionFor changes
  useEffect(() => {
    setLineItems([{
      ...createEmptyLineItem(),
      id: '1'
    }]);
  }, [formData.requisitionFor]);
  const isGoods = formData.requisitionFor === 'Goods';
  const handleLineItemChange = (id: string, field: string, value: any) => {
    setLineItems(items => items.map(item => {
      if (item.id === id) {
        const updated = {
          ...item,
          [field]: value
        };
        if (field === 'qty' || field === 'rate') {
          updated.estimatedCost = isGoods ? updated.qty * updated.rate : updated.rate;
        }
        // Validate qty against APP plan qty
        if (field === 'qty' && updated.appReference) {
          const appItem = mockAppItems.find(a => a.id === updated.appReference);
          if (appItem && Number(value) > appItem.qty) {
            alert(`Warning: Quantity (${value}) exceeds the Annual Procurement Plan quantity (${appItem.qty}) for "${appItem.name}". You may still proceed.`);
          }
        }
        return updated;
      }
      return item;
    }));
  };
  const handleProductSelect = (id: string, selectedValue: string) => {
    if (isGoods) {
      const product = mockProducts.find(p => p.productCode === selectedValue);
      if (product) {
        setLineItems(items => items.map(item => {
          if (item.id === id) {
            const rate = product.defaultRate || 0;
            return {
              ...item,
              product: product.productCode,
              rate: rate,
              estimatedCost: item.qty * rate
            };
          }
          return item;
        }));
      }
    }
  };
  const addLineItem = () => {
    setLineItems([...lineItems, createEmptyLineItem()]);
  };
  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter(item => item.id !== id));
    }
  };
  const openAppModal = (id: string) => {
    setActiveLineItemId(id);
    setIsAppModalOpen(true);
  };
  const handleAppSelect = (appId: string) => {
    if (activeLineItemId) {
      handleLineItemChange(activeLineItemId, 'appReference', appId);
    }
    setIsAppModalOpen(false);
    setActiveLineItemId(null);
  };
  const totalAmount = lineItems.reduce((sum, item) => sum + item.estimatedCost, 0);
  const getColumnHeader = () => {
    switch (formData.requisitionFor) {
      case 'Goods':
        return 'Product';
      case 'Works':
        return 'Works Description';
      case 'Service':
        return 'Service Description';
      default:
        return 'Item';
    }
  };
  const renderItemCell = (item: (typeof lineItems)[0]) => {
    return <div className="space-y-1" data-id="element-1608">
        {isGoods ? <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 bg-white" value={item.product} onChange={e => handleProductSelect(item.id, e.target.value)} data-id="element-1609">
            <option value="" data-id="element-1610">Select Product...</option>
            {mockProducts.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.productCode} data-id="element-1611">
                  {p.productCode} - {p.productName}
                </option>)}
          </select> : <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.product} onChange={e => handleLineItemChange(item.id, 'product', e.target.value)} placeholder={formData.requisitionFor === 'Works' ? 'Enter works description...' : 'Enter service description...'} data-id="element-1612" />}
        {item.appReference && <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1613">
            <LinkIcon className="w-3 h-3 mr-1" data-id="element-1614" />
            {item.appReference}
          </div>}
      </div>;
  };
  const handleSubmitPR = () => {
    if (!formData.departmentCode) {
      alert('Please select a department.');
      return;
    }
    if (!formData.project) {
      alert('Please select a project.');
      return;
    }
    const validItems = lineItems.filter(item => item.product !== '');
    if (validItems.length === 0) {
      alert(`Please add at least one ${formData.requisitionFor.toLowerCase()} item.`);
      return;
    }
    if (isGoods) {
      const invalidQty = validItems.some(item => item.qty <= 0);
      if (invalidQty) {
        alert('All selected items must have a quantity greater than 0.');
        return;
      }
    }
    alert('PR Submitted Successfully!');
    navigate('/purchase-requisitions');
  };
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-1615">
      <div className="flex items-center justify-between" data-id="element-1616">
        <div data-id="element-1617">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1618">
            Create Purchase Requisition
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1619">
            Fill in the details to initiate a new procurement request.
          </p>
        </div>
      </div>

      <form className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 md:p-8" data-id="element-1620">
        <FormSection title="Basic Information" data-id="element-1621">
          <DisplayField label="PR No" value={formData.prNo} data-id="element-1622" />
          <DisplayField label="Originator" value={formData.originator} data-id="element-1623" />

          <SelectField label="Department" name="departmentCode" value={formData.departmentCode} onChange={e => setFormData({
          ...formData,
          departmentCode: e.target.value
        })} required options={[{
          value: 'IT-01',
          label: 'Information Technology'
        }, {
          value: 'HR-02',
          label: 'Human Resources'
        }]} data-id="element-1624" />

          <SelectField label="Project" name="project" value={formData.project} onChange={e => setFormData({
          ...formData,
          project: e.target.value
        })} required options={[{
          value: 'PRJ-IT-100',
          label: 'PRJ-IT-100 (IT Infrastructure)'
        }, {
          value: 'PRJ-HR-200',
          label: 'PRJ-HR-200 (HR Operations)'
        }, {
          value: 'PRJ-FIN-300',
          label: 'PRJ-FIN-300 (Finance Systems)'
        }, {
          value: 'PRJ-OPS-400',
          label: 'PRJ-OPS-400 (Operations)'
        }, {
          value: 'PRJ-MKT-500',
          label: 'PRJ-MKT-500 (Marketing)'
        }]} data-id="element-1625" />

          <SelectField label="Priority" name="priority" value={formData.priority} onChange={e => setFormData({
          ...formData,
          priority: e.target.value
        })} options={[{
          value: 'Low',
          label: 'Low'
        }, {
          value: 'Medium',
          label: 'Medium'
        }, {
          value: 'High',
          label: 'High'
        }]} data-id="element-1626" />

          <TextField label="Estimated Delivery Date" name="estimatedDeliveryDate" type="date" value={formData.estimatedDeliveryDate} onChange={e => setFormData({
          ...formData,
          estimatedDeliveryDate: e.target.value
        })} required data-id="element-1627" />

          <SelectField label="Procurement Category" name="requisitionFor" value={formData.requisitionFor} onChange={e => setFormData({
          ...formData,
          requisitionFor: e.target.value
        })} required options={[{
          value: 'Goods',
          label: 'Goods'
        }, {
          value: 'Works',
          label: 'Works'
        }, {
          value: 'Service',
          label: 'Service'
        }]} data-id="element-1628" />

          <SelectField label="Fiscal Year" name="fiscalYear" value={appFiscalYear} onChange={e => setAppFiscalYear(e.target.value)} options={[{
          value: 'FY 2025/26',
          label: 'FY 2025/26'
        }, {
          value: 'FY 2024/25',
          label: 'FY 2024/25'
        }]} data-id="element-1629" />

          <SelectField label="Procurement Method" name="procurementMethod" value={formData.procurementMethod} onChange={e => setFormData({
          ...formData,
          procurementMethod: e.target.value
        })} required options={[{
          value: 'Direct Purchase',
          label: 'Direct Purchase'
        }, {
          value: 'Open Tender',
          label: 'Open Tender'
        }, {
          value: 'Short Purchase',
          label: 'Short Purchase'
        }]} data-id="element-1630" />
        </FormSection>

        <div className="mb-8" data-id="element-1631">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200" data-id="element-1632">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1633">Line Items</h3>
            <button type="button" onClick={addLineItem} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200" data-id="element-1634">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-1635" /> Add Item
            </button>
          </div>

          <div className="overflow-x-auto" data-id="element-1636">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-1637">
              <thead className="bg-gray-50" data-id="element-1638">
                <tr data-id="element-1639">
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1640">
                    {getColumnHeader()}
                  </th>
                  {isGoods && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1641">
                      Qty
                    </th>}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1642">
                    {isGoods ? 'Rate' : 'Price'}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-32" data-id="element-1643">
                    Est. Cost
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1644">
                    Specification
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24" data-id="element-1645">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1646">
                {lineItems.map(item => <tr key={item.id} data-id="element-1647">
                    <td className="px-4 py-2 align-top" data-id="element-1648">
                      {renderItemCell(item)}
                    </td>
                    {isGoods && <td className="px-4 py-2 align-top" data-id="element-1649">
                        <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.qty} onChange={e => handleLineItemChange(item.id, 'qty', Number(e.target.value))} data-id="element-1650" />
                      </td>}
                    <td className="px-4 py-2 align-top" data-id="element-1651">
                      <input type="number" min="0" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.rate} onChange={e => handleLineItemChange(item.id, 'rate', Number(e.target.value))} data-id="element-1652" />
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50 align-top" data-id="element-1653">
                      ${item.estimatedCost.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 align-top" data-id="element-1654">
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5" value={item.specification} onChange={e => handleLineItemChange(item.id, 'specification', e.target.value)} data-id="element-1655" />
                    </td>
                    <td className="px-4 py-2 text-center align-top" data-id="element-1656">
                      <div className="flex items-center justify-center space-x-2" data-id="element-1657">
                        <button type="button" onClick={() => openAppModal(item.id)} className="text-indigo-600 hover:text-indigo-800 p-1" title="Tag Annual Procurement Plan" data-id="element-1658">
                          <LinkIcon className="h-4 w-4" data-id="element-1659" />
                        </button>
                        <button type="button" onClick={() => removeLineItem(item.id)} className="text-red-500 hover:text-red-700 p-1" data-id="element-1660">
                          <TrashIcon className="h-4 w-4" data-id="element-1661" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-1662">
                <tr data-id="element-1663">
                  <td colSpan={isGoods ? 3 : 2} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-1664">
                    Total Amount:
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-indigo-600" data-id="element-1665">
                    ${totalAmount.toLocaleString()}
                  </td>
                  <td colSpan={2} data-id="element-1666"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200" data-id="element-1667">
          <div className="flex justify-end space-x-3" data-id="element-1668">
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1669">
              Cancel
            </button>
            <button type="button" className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100" data-id="element-1670">
              Save as Draft
            </button>
            <button type="button" onClick={handleSubmitPR} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-1671">
              Submit PR
            </button>
          </div>
        </div>
      </form>

      {/* APP Selection Modal */}
      {isAppModalOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1672">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1673">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setIsAppModalOpen(false)} data-id="element-1674">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-1675"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1676">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-1677">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1678">
                <div className="w-full" data-id="element-1679">
                  <div className="flex justify-between items-center mb-4" data-id="element-1680">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1681">
                      APP — {appFiscalYear}
                    </h3>
                    <button onClick={() => setIsAppModalOpen(false)} className="text-gray-400 hover:text-gray-500" data-id="element-1682">
                      <XIcon className="h-5 w-5" data-id="element-1683" />
                    </button>
                  </div>
                  <div className="overflow-x-auto" data-id="element-1684">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-1685">
                      <thead className="bg-gray-50" data-id="element-1686">
                        <tr data-id="element-1687">
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1688">
                            Type
                          </th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1689">
                            Item
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase w-20" data-id="element-1690">
                            Qty
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-1691">
                            Unit Price
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-1692">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-1693">
                        {mockAppItems.filter(app => app.type === formData.requisitionFor && app.fiscalYear === appFiscalYear).map(app => {
                      const typeColor = app.type === 'Goods' ? 'bg-blue-100 text-blue-800' : app.type === 'Works' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800';
                      return <tr key={app.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleAppSelect(app.id)} data-id="element-1694">
                                <td className="px-4 py-3" data-id="element-1695">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`} data-id="element-1696">
                                    {app.type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1697">
                                  {app.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1698">
                                  {app.type === 'Goods' ? app.qty : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-1699">
                                  ${app.unitPrice.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right" data-id="element-1700">
                                  ${app.total.toLocaleString()}
                                </td>
                              </tr>;
                    })}
                        {mockAppItems.filter(app => app.type === formData.requisitionFor && app.fiscalYear === appFiscalYear).length === 0 && <tr data-id="element-1701">
                            <td colSpan={5} className="py-6 text-center text-sm text-gray-500" data-id="element-1702">
                              No matching items found for{' '}
                              {formData.requisitionFor} in {appFiscalYear}.
                            </td>
                          </tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-1703">
                <button type="button" onClick={() => setIsAppModalOpen(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1704">
                  Cancel
                </button>
                <button type="button" onClick={() => handleAppSelect('')} className="mt-3 w-full inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-red-50 text-base font-medium text-red-700 hover:bg-red-100 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1705">
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}